import Axios from 'axios'
import moment from 'moment'
import appConfig from '../appConfig'
import get from 'lodash/get'

class DeridocClient {
  constructor(token = null, basicAuth = null) {
    let auth = null
    if (token !== null) {
      auth = 'Bearer ' + token
    } else if (basicAuth !== null) {
      auth = 'Basic ' + basicAuth
    }

    this.client = Axios.create({
      headers: {
        'Authorization': auth,
        'Content-Type': 'application/json'
      }
    })
  }

  get(url) {
    return this.client.get(url).catch(this.handleError)
  }

  post(url, data) {
    return this.client.post(url, data).catch(this.handleError)
  }

  handleError = error => {
    // console.log(error)
    _LTracker.push({
        application: 'deridoc',
        environment: process.env.NODE_ENV === 'production' ? 'production' : 'test',
        severity: 'FATAL',
        error: {message: error.toString(), details: error.toJSON()}
      }
    )
  }
}

export default {
  /**
   * Get products from DERIFIN DB by WKN
   *
   * @param {string} ids
   * @returns {AxiosPromise}
   */
  getProducts(ids) {
    return Axios.post('/api/products', {
      'customCriteria': [
        {
          'key': 'ids.wkn',
          'operator': 'IN',
          'value': ids
        }
      ]
    })
  },

  /**
   * Get single product from DERIFIN DB
   *
   * @param {string} id
   * @returns {AxiosPromise}
   */
  getProduct(id) {
    return DeridocClient.get('/api/product/' + id)
  },


  /**
   * Get single product from SPK APPROC
   *
   * @param {string} id
   * @param token
   * @returns {AxiosPromise}
   */
  getApproval(id, token) {
    return new DeridocClient(token).get('/api/approval/' + id)
  },

  /**
   * Get single product from SPK APPROC
   *
   * @returns {AxiosPromise}
   * @param language
   * @param type
   * @param token
   */
  getDdf(language, type, token) {
    if(type !== undefined) {
      return new DeridocClient(token).get(`/api/ddf/${language}/type/${type}`)
    } else {
      return new DeridocClient(token).get(`/api/ddf/${language}`)
    }
  },


  /**
   *
   * @param {string} dq
   * @returns {AxiosPromise}
   */
  getProductHistory(dq) {
    let dateFormat = 'YYYY-MM-DD'
    let data = {
      'dq': dq,
      'from': moment().subtract(5, 'years').format(dateFormat),
      'to': moment().format(dateFormat)
    }
    return new DeridocClient().post('/api/productHistory', data)
  },

  stockmarketMap: {
    QXXFRA: 'Frankfurt a.M.',
    QXXSTU: 'Stuttgart'
  },

  /**
   *
   * @param environment
   * @param appInstance
   * @param {Array} dqs
   * @param {string} token
   * @param context
   * @returns {AxiosPromise}
   */
  getProductsFromBroker(environment, appInstance, dqs, token, context) {

    let data = {
      'dqs': dqs,
      'environment': get(appConfig, 'environmentMap.' + environment, appInstance),
      'context': context
    }
    if (dqs[0].includes('QXPR')) {
      data.type = 'DPC_CONVERSATION_DERIVATE'
    }
    return new DeridocClient(token).post('/api/brokerProducts/' + get(appConfig, 'environmentMap.' + environment, appInstance) + '/m/product/onepager', data)
  },

  /**
   *
   * @param environment
   * @param appInstance
   * @param shortlistId
   * @param token
   * @param dqs
   * @returns {Promise<AxiosResponse<T>>}
   */
  getShortlist(environment, appInstance, shortlistId, token, dqs = null) {
    let suffix = dqs ? '?dqs=' + dqs.split(' ').join(',') : ''

    return new DeridocClient(token).get(`/api/brokerProducts/${get(appConfig, 'environmentMap.' + environment, appInstance)}/m/shortlist/${shortlistId}/export${suffix}`)
  },

  /**
   *
   * @param environment
   * @param appInstance
   * @param token
   * @param dqs
   * @returns {Promise<AxiosResponse<T>>}
   */
  getMultiProductReport(environment, appInstance, token, dqs) {

    let data = {
      'dqs': dqs,
    }
    return new DeridocClient(token).post(`/api/brokerProducts/${get(appConfig, 'environmentMap.' + environment, appInstance)}/m/product/multi-product-report`, data)
  },

  /**
   *
   * @param environment
   * @param appInstance
   * @param token
   * @param data
   * @returns {Promise<AxiosResponse<any>>}
   */
  getTmcExport(environment, appInstance, token, data) {
    return new DeridocClient(token).post(`/api/brokerProducts/${get(appConfig, 'environmentMap.' + environment, appInstance)}/m/target-market/report`, data)
  },

  /**
   *
   * @param environment
   * @param appInstance
   * @param id
   * @param token
   * @returns {Promise<AxiosResponse<any>>}
   */
  getTmcHistoryExport(environment, appInstance, id, token, ) {
    return new DeridocClient(token).get(`/api/brokerProducts/${get(appConfig, 'environmentMap.' + environment, appInstance)}/m/list-tmc-report/_product_list_tcm_report_default/report/${id}`)
  },

  /**
   *
   * @param environment
   * @param appInstance
   * @param token
   * @param watchlistId
   * @param comparisonMode
   * @returns {Promise<AxiosResponse<any>>}
   */
  getMultiTmcExport(environment, appInstance, token, watchlistId, comparisonMode) {
    return new DeridocClient(token).get(`/api/brokerProducts/${get(appConfig, 'environmentMap.' + environment, appInstance)}/m/watchlist/${watchlistId}/target-market-criteria-check/comparisonMode/${comparisonMode}?includeCustomProducts=0`)
  },


  /**
   *
   * @param environment
   * @param appInstance
   * @param {string} dq
   * @param token
   * @param options
   */
  getProductForReport(environment, appInstance, dq, token, options) {
    let params = options.length === 0 ? '' : options.reduce((sum, el) => {
      if (sum !== '?') {
        sum = sum + '&'
      }
      return sum + el.name + '=' + el.value
    }, '?')
    return new DeridocClient(token).get(
      ['api/reportProduct',
        get(appConfig, 'environmentMap.' + environment, appInstance),
        'm/report',
        dq
      ].join('/') + params)
  },

  /**
   *
   * @param environment
   * @param appInstance
   * @param watchlistId
   * @param ecId
   * @param token
   * @returns {Promise<AxiosResponse<any>>}
   */
  getEquivalenceCheck(environment, appInstance, watchlistId, ecId, token) {
    return new DeridocClient(token).get(
      `api/brokerProducts/${get(appConfig, 'environmentMap.' + environment, appInstance)}/m/watchlist/${watchlistId}/equivalence-check/${ecId}/export`,
    )
  },

  /**
   *
   * @param environment
   * @param appInstance
   * @param ecId
   * @param token
   * @returns {Promise<AxiosResponse<any>>}
   */
  getProductEquivalenceCheck(environment, appInstance, ecId, token) {
    return new DeridocClient(token).get(
      `api/brokerProducts/${get(appConfig, 'environmentMap.' + environment, appInstance)}/product/equivalence-check/${ecId}/entry`,
    )
  },

  /**
   *
   * @param environment
   * @param appInstance
   * @param watchlistId
   * @param ecId
   * @param token
   * @returns {Promise<AxiosResponse<any>>}
   */
  getEquivalenceCheckStructure(environment, appInstance, watchlistId, ecId, token) {
    return new DeridocClient(token).get(
      `api/brokerProducts/${get(appConfig, 'environmentMap.' + environment, appInstance)}/m/watchlist/${watchlistId}/equivalence-check/${ecId}/export/structure`,
    )
  },

  /**
   *
   * @param environment
   * @param appInstance
   * @param {string} dq
   * @param token
   * @param options
   * @param fields
   */
  getProductForFactsheet(environment, appInstance, dq, token, options, fields) {
    let params = options.length === 0 ? '' : options.reduce((sum, el) => {
      if (sum !== '?') {
        sum = sum + '&'
      }
      return sum + el.name + '=' + el.value
    }, '?')

    return new DeridocClient(token).post(
      ['api/factsheet',
        get(appConfig, 'environmentMap.' + environment, appInstance),
        'product',
        dq,
        'specified'
      ].join('/') + params,
      fields)
  },

  /**
   *
   * @param environment
   * @param appInstance
   * @param data
   * @param token
   * @param dqs
   */
  getWhitelabel(environment, appInstance, data, token, dqs) {
    return new DeridocClient(token).post(
      `api/brokerProducts/${get(appConfig, 'environmentMap.' + environment, appInstance)}/m/product-compare/export?dqs=${dqs}`,
      data
    )
  },

  getPortfolio(environment, moduleId, simulationId, figuresId, token) {
    return new DeridocClient(token).get(
      `/api/brokerProducts/${environment}/m/portfolio-simulator/${moduleId}/simulation/export?simulationId=${simulationId}&figuresSessionId=${figuresId}`,
    )
  }
}



// WEBPACK FOOTER //
// ./src/service/ProductService.js